import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Award, GraduationCap, ArrowUpRight } from "lucide-react";
import { education, certifications } from "../data/portfolio";

const ease = [0.16, 1, 0.3, 1];

function EducationSnap() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], [180, -180]);
  const bgOpacity = useTransform(
    scrollYProgress,
    [0, 0.25, 0.75, 1],
    [0, 0.045, 0.045, 0]
  );
  const cardY = useTransform(scrollYProgress, [0, 1], [50, -50]);

  return (
    <div
      ref={ref}
      data-snap-page
      className="relative h-[100svh] flex items-center overflow-hidden"
    >
      <motion.div
        aria-hidden
        style={{ y: bgY, opacity: bgOpacity }}
        className="pointer-events-none absolute left-[-3vw] top-1/2 -translate-y-1/2 font-display text-[40vw] md:text-[22vw] lg:text-[18vw] leading-none tracking-[-0.06em] text-ink select-none"
      >
        education.
      </motion.div>

      <div className="relative w-full max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-12 gap-x-8 gap-y-6">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.9, ease }}
            className="col-span-12 md:col-span-4"
          >
            <p className="font-body text-[10px] md:text-xs tracking-[0.4em] uppercase text-ink-2">
              06 — Credentials
            </p>
            <h2 className="mt-6 font-display text-3xl md:text-5xl lg:text-6xl tracking-[-0.03em] leading-[1.05] text-ink">
              Where it{" "}
              <span className="text-metal">started.</span>
            </h2>
          </motion.div>

          <motion.div
            style={{ y: cardY }}
            className="col-span-12 md:col-span-8"
          >
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.9, ease, delay: 0.1 }}
              data-testid="education-card"
              className="p-8 md:p-12 rounded-2xl border border-line/5 bg-surface-2 shadow-[0_30px_80px_-40px_rgba(0,0,0,0.4)]"
            >
            <GraduationCap
              className="w-7 h-7 text-ink-metal mb-8"
              strokeWidth={1.3}
            />
            <p className="font-body text-[10px] tracking-[0.3em] uppercase text-ink-2">
              Education
            </p>
            <h3 className="mt-3 font-display text-2xl md:text-4xl lg:text-5xl tracking-[-0.02em] text-ink leading-tight">
              {education.school}
            </h3>
            <p className="mt-3 font-display text-base md:text-xl text-ink-metal">
              {education.degree}
            </p>
            <p className="mt-2 font-body text-xs md:text-sm text-ink-2">
              {education.period} · {education.location}
            </p>
            <ul className="mt-8 space-y-3">
              {education.details.map((d, i) => (
                <motion.li
                  key={d}
                  initial={{ opacity: 0, x: 10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.6, ease, delay: 0.3 + i * 0.1 }}
                  className="font-body text-sm md:text-base text-ink-2 leading-relaxed pl-5 relative before:content-[''] before:absolute before:left-0 before:top-[0.7em] before:w-2.5 before:h-px before:bg-ink-3"
                >
                  {d}
                </motion.li>
              ))}
            </ul>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}

function CertificationsSnap() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], [180, -180]);
  const bgOpacity = useTransform(
    scrollYProgress,
    [0, 0.25, 0.75, 1],
    [0, 0.045, 0.045, 0]
  );
  const cardY = useTransform(scrollYProgress, [0, 1], [50, -50]);

  return (
    <div
      ref={ref}
      id="certifications"
      data-snap-page
      className="relative h-[100svh] flex items-center overflow-hidden scroll-mt-24"
    >
      <motion.div
        aria-hidden
        style={{ y: bgY, opacity: bgOpacity }}
        className="pointer-events-none absolute right-[-3vw] top-1/2 -translate-y-1/2 font-display text-[40vw] md:text-[22vw] lg:text-[18vw] leading-none tracking-[-0.06em] text-ink select-none"
      >
        certified.
      </motion.div>

      <div className="relative w-full max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-12 gap-x-8 gap-y-6">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.9, ease }}
            className="col-span-12 md:col-span-4"
          >
            <p className="font-body text-[10px] md:text-xs tracking-[0.4em] uppercase text-ink-2">
              06 — Credentials
            </p>
            <h2 className="mt-6 font-display text-3xl md:text-5xl lg:text-6xl tracking-[-0.03em] leading-[1.05] text-ink">
              Validated.{" "}
              <span className="text-metal">By the vendor.</span>
            </h2>
            <p className="mt-6 font-body text-sm md:text-base text-ink-2 leading-relaxed max-w-md">
              Two Microsoft certifications — proof that the work and the
              paperwork line up.
            </p>
          </motion.div>

          <motion.div
            style={{ y: cardY }}
            className="col-span-12 md:col-span-8"
          >
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.9, ease, delay: 0.1 }}
              data-testid="certifications-card"
              className="p-8 md:p-12 rounded-2xl border border-line/5 bg-surface-2 shadow-[0_30px_80px_-40px_rgba(0,0,0,0.4)]"
            >
            <Award className="w-7 h-7 text-ink-metal mb-8" strokeWidth={1.3} />
            <p className="font-body text-[10px] tracking-[0.3em] uppercase text-ink-2">
              Microsoft Certifications
            </p>
            <h3 className="mt-3 font-display text-2xl md:text-3xl tracking-[-0.02em] text-ink leading-tight">
              Two badges, one platform.
            </h3>

            <div className="mt-8 space-y-3">
              {certifications.map((c, i) => (
                <motion.a
                  key={c.id}
                  id={c.id}
                  href={c.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`cert-item-${i}`}
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.6, ease, delay: 0.3 + i * 0.12 }}
                  className="group flex items-center gap-4 p-4 md:p-5 rounded-xl border border-line/5 bg-line/[0.02] hover:border-line/20 hover:bg-line/[0.06] transition-all duration-300 scroll-mt-28"
                >
                  <div className="w-10 h-10 rounded-full grid place-items-center bg-line/5 border border-line/10 shrink-0">
                    <span className="font-display text-xs text-ink-metal">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                  </div>
                  <p className="flex-1 font-body text-sm md:text-base text-ink leading-snug">
                    {c.name}
                  </p>
                  <ArrowUpRight className="w-4 h-4 text-ink-2 group-hover:text-ink transition-colors" />
                </motion.a>
              ))}
            </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}

export default function Education() {
  return (
    <section data-testid="education-section" className="relative bg-surface">
      <EducationSnap />
      <CertificationsSnap />
    </section>
  );
}
